import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { Database, Layers, Search } from "lucide-react"
import { type ReactNode, useMemo, useState } from "react"
import { type Column, HairlineTable } from "@/components/archetypes/HairlineTable"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { Button } from "@/components/ui/button"
import { Screen } from "@/components/ui/screen"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip } from "@/components/ui/tooltip"
import { useLocale, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { type ObjectResponse, useObjectPages, useOntology, useReferenceIndex, useTitleOf } from "./hooks"
import { HowItWorks } from "./how-it-works"
import { CreateCommands } from "./record-commands"
import { areaOfType, listFields, optionLabel, recordTitle } from "./ontology"
import { FieldValue, HiddenChip, isMasked, RefusalSummary, StatusPill, useFormatters } from "./values"

export function CollectionPage() {
  const { objectType } = useParams({ from: "/$orgId/$environmentId/records/$objectType" })
  return <Collection key={objectType} objectType={objectType} />
}

function Collection({ objectType }: { objectType: string }) {
  const t = useT()
  const scope = useScope()
  const navigate = useNavigate()
  const fmt = useFormatters()
  const { preference } = useLocale()
  const { ontology, defs } = useOntology(scope)
  const type = ontology?.types.get(objectType)
  const area = ontology ? areaOfType(ontology, objectType) : undefined
  const pages = useObjectPages(scope, objectType)
  const rows = useMemo(() => pages.data?.pages.flatMap((p) => p.items) ?? [], [pages.data])
  const [query, setQuery] = useState("")
  const [status, setStatus] = useState<string | null>(null)
  const titleOf = useTitleOf(ontology)
  const columns = useMemo(() => (type ? listFields(type, 5) : []), [type])
  const statusField = type?.fields.find((f) => f.id === type.statusField)

  // References in view resolve to their records' titles (each read authorized by the cell).
  const refTargets = useMemo(() => {
    if (!type) return []
    const refCols = columns.filter((c) => c.role === "reference")
    return rows.flatMap((row) =>
      refCols.flatMap((c) => {
        const v = (row.values as Record<string, unknown>)[c.id]
        const link = type.links.find((l) => l.id === c.link)
        return typeof v === "string" && link && !isMasked(row.masked as Record<string, unknown>, c.id) ? [{ objectType: link.target, key: v }] : []
      }),
    )
  }, [rows, columns, type])
  const refs = useReferenceIndex(scope, refTargets)
  const refErrors = refTargets.map((r) => refs(r.objectType, r.key)).filter((r) => r.isError).map((r) => r.error)

  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>()
    if (!statusField) return counts
    for (const row of rows) {
      if (isMasked(row.masked as Record<string, unknown>, statusField.id)) continue
      const v = (row.values as Record<string, unknown>)[statusField.id]
      if (typeof v === "string") counts.set(v, (counts.get(v) ?? 0) + 1)
    }
    return counts
  }, [rows, statusField])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter((row) => {
      const values = row.values as Record<string, unknown>
      if (status && statusField && values[statusField.id] !== status) return false
      if (!q) return true
      if (row.key.toLowerCase().includes(q)) return true
      return Object.entries(values).some(([k, v]) => typeof v === "string" && !isMasked(row.masked as Record<string, unknown>, k) && v.toLowerCase().includes(q))
    })
  }, [rows, query, status, statusField])

  const tableColumns: Column<ObjectResponse>[] = [
    {
      key: "title",
      header: type?.label ?? t("ws.list.record"),
      cellClassName: "max-w-80",
      cell: (row) => {
        const values = row.values as Record<string, unknown>
        const title = recordTitle(type, values, row.masked as Record<string, unknown> | undefined, preference.locale)
        return (
          <Link
            to="/$orgId/$environmentId/records/$objectType/$key"
            params={{ ...scope, objectType: row.objectType, key: row.key }}
            onClick={(e) => e.stopPropagation()}
            className="block min-w-0 outline-none"
          >
            {title.title ? (
              <>
                <span dir="auto" className="block truncate font-medium text-foreground">
                  {title.title}
                </span>
                <span dir="ltr" className="block truncate text-xs text-muted-foreground">
                  {row.key}
                </span>
              </>
            ) : (
              <span className="flex items-center gap-2">
                <span dir="ltr" className="truncate font-medium text-foreground">
                  {row.key}
                </span>
                {title.hidden ? <HiddenChip className="h-5 text-2xs" /> : null}
              </span>
            )}
          </Link>
        )
      },
    },
    ...columns.map(
      (field): Column<ObjectResponse> => ({
        key: field.id,
        header: field.label,
        align: field.role === "money" || field.role === "quantity" ? "end" : "start",
        cellClassName: field.role === "text" ? "max-w-64 truncate" : undefined,
        cell: (row) => {
          const values = row.values as Record<string, unknown>
          const masked = row.masked as Record<string, unknown> | undefined
          if (field.role === "reference" && type && !isMasked(masked, field.id)) {
            const v = values[field.id]
            const link = type.links.find((l) => l.id === field.link)
            if (typeof v === "string" && link) {
              const result = refs(link.target, v)
              const title = titleOf(result?.data, v)
              return (
                <Link
                  to="/$orgId/$environmentId/records/$objectType/$key"
                  params={{ ...scope, objectType: link.target, key: v }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-foreground/90 underline decoration-border underline-offset-4 hover:decoration-foreground"
                >
                  {result?.isPending ? <Skeleton className="inline-block h-3 w-20" /> : title.isKey ? <span dir="ltr">{v}</span> : title.title}
                </Link>
              )
            }
          }
          return <FieldValue field={field} values={values} masked={masked} type={type} />
        },
      }),
    ),
  ]

  const count = pages.hasNextPage ? t("ws.list.loadedMore", { count: rows.length }) : t("ws.list.total", { count: rows.length })

  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="home" to="/$orgId/$environmentId" params={scope}>
            {t("ws.nav.home")}
          </Link>,
          ...(area
            ? [
                <Link key="area" to="/$orgId/$environmentId/areas/$areaId" params={{ ...scope, areaId: area.id }}>
                  {area.label}
                </Link>,
              ]
            : []),
          type?.pluralLabel ?? objectType,
        ]}
        actions={
          <>
            <HowItWorks lines={["ws.how.list"]} />
            <Tooltip content={t("ws.list.inspect")}>
              <Button size="icon-sm" variant="ghost" asChild>
                <Link to="/$orgId/$environmentId/objects/$objectType" params={{ ...scope, objectType }} aria-label={t("ws.list.inspect")}>
                  <Layers />
                </Link>
              </Button>
            </Tooltip>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width="board" className="space-y-6 px-4 py-8 md:px-8">
          <header className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <div className="min-w-0 flex-1 space-y-1">
              <h1 className="text-[1.75rem] leading-tight font-semibold tracking-[-0.02em]">{ontology ? (type?.pluralLabel ?? objectType) : <Skeleton className="h-8 w-48" />}</h1>
              <p className="text-sm text-muted-foreground">
                {pages.isPending || !ontology ? <Skeleton className="inline-block h-3 w-24" /> : count}
                {type?.source ? <span className="text-subtle-foreground"> · {t("ws.list.importedFrom", { source: type.source.split(".")[0] ?? type.source })}</span> : null}
              </p>
            </div>
            <label className="relative block w-full sm:w-72">
              <span className="sr-only">{t("ws.list.filter")}</span>
              <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-subtle-foreground" aria-hidden />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("ws.list.filterPlaceholder", { name: (type?.pluralLabel ?? objectType).toLowerCase() })}
                className="h-9 w-full rounded-full border border-input bg-background ps-9 pe-3 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
              />
            </label>
          </header>
          {type && defs.data ? <CreateCommands scope={scope} type={type} artifacts={defs.data.artifacts} ontology={ontology} /> : null}
          {statusCounts.size > 1 ? (
            <div role="toolbar" aria-label={t("ws.list.byStatus")} className="flex flex-wrap items-center gap-1.5">
              <FilterChip active={status === null} onClick={() => setStatus(null)} label={t("common.all")} count={fmt.number(rows.length)} />
              {[...statusCounts.entries()]
                .sort((a, b) => (statusField?.options.findIndex((o) => o.value === a[0]) ?? 0) - (statusField?.options.findIndex((o) => o.value === b[0]) ?? 0))
                .map(([value, n]) => (
                  <FilterChip key={value} active={status === value} onClick={() => setStatus(status === value ? null : value)} label={<StatusPill value={value} field={statusField} type={type} className="h-auto bg-transparent px-0" />} count={fmt.number(n)} ariaLabel={statusField?.options.find((o) => o.value === value)?.label ?? optionLabel(value)} />
                ))}
            </div>
          ) : null}
          <RefusalSummary errors={refErrors} title={t("ws.related.unreadable")} />
          {defs.isError ? (
            <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
          ) : pages.isPending || !ontology ? (
            <TableSkeleton rows={10} columns={Math.max(3, columns.length + 1)} />
          ) : pages.isError ? (
            <ErrorState error={pages.error} what={type?.pluralLabel ?? objectType} onRetry={() => void pages.refetch()} />
          ) : rows.length === 0 ? (
            <div data-ws-empty="">
              <EmptyState icon={<Database />} title={t("ws.list.empty", { name: (type?.pluralLabel ?? objectType).toLowerCase() })}>
                <p>{t("ws.list.emptyHelp")}</p>
              </EmptyState>
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-border">
                <HairlineTable
                  rows={visible}
                  getRowKey={(row) => `${row.objectType}/${row.key}`}
                  minWidthClassName="min-w-[720px]"
                  onRowClick={(row) => void navigate({ to: "/$orgId/$environmentId/records/$objectType/$key", params: { ...scope, objectType: row.objectType, key: row.key } })}
                  columns={tableColumns}
                />
              </div>
              {visible.length === 0 ? <p className="text-sm text-muted-foreground">{t("ws.list.noMatch")}</p> : null}
              {pages.hasNextPage ? (
                <div className="flex justify-center">
                  <Button size="sm" variant="outline" disabled={pages.isFetchingNextPage} onClick={() => void pages.fetchNextPage()}>
                    {pages.isFetchingNextPage ? t("common.working") : t("ws.list.loadMore")}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Screen>
      </div>
    </>
  )
}

function FilterChip({ active, onClick, label, count, ariaLabel }: { active: boolean; onClick: () => void; label: ReactNode; count: string; ariaLabel?: string }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={ariaLabel ? `${ariaLabel} ${count}` : undefined}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
        active ? "border-foreground/30 bg-accent text-foreground" : "border-border text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
      <span className="tabular-nums text-subtle-foreground">{count}</span>
    </button>
  )
}
